import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function SageChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get("/sage/history").then(({ data }) => {
        if (data.length === 0) {
          setMessages([{ role: "assistant", content: "Hi, I'm Sage. Ask me anything about your practice — draft letters, check codes, summarize records, or get documentation tips." }]);
        } else {
          setMessages(data);
        }
      }).catch(() => {
        setMessages([{ role: "assistant", content: "Hi, I'm Sage. How can I help today?" }]);
      });
    }
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const msg = input;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/sage/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.response }]);
    } catch (err) {
      toast.error("Sage is unavailable right now");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed cw-fab-sage right-5 md:right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-40 transition-transform hover:scale-105"
          style={{ background: "var(--mf-primary)" }}
          data-testid="sage-chat-open"
          aria-label="Open Sage AI"
        >
          <Sparkles className="w-6 h-6 text-white" strokeWidth={1.75} />
        </button>
      )}

      {open && (
        <div
          className="fixed cw-sage-panel right-3 md:right-6 w-[calc(100vw-1.5rem)] sm:w-96 rounded-2xl flex flex-col z-40 overflow-hidden"
          style={{ background: "var(--mf-surface)", boxShadow: "0 20px 60px rgba(15,27,45,0.2)" }}
          data-testid="sage-chat-panel"
        >
          <div className="flex items-center justify-between px-5 py-4" style={{ background: "var(--mf-primary)", color: "#FAFAF8" }}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" strokeWidth={1.75} />
              <div>
                <div className="font-serif text-lg leading-none">Sage</div>
                <div className="text-xs opacity-80">AI Assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="sage-chat-close" className="p-1 rounded-lg hover:bg-white/10">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    background: m.role === "user" ? "var(--mf-primary)" : "var(--mf-muted)",
                    color: m.role === "user" ? "#FAFAF8" : "var(--mf-text)"
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="px-4 py-2.5 rounded-2xl text-sm" style={{ background: "var(--mf-muted)", color: "var(--mf-text-muted)" }}>Thinking…</div></div>}
            <div ref={endRef}></div>
          </div>

          <form onSubmit={send} className="p-3 border-t flex gap-2" style={{ borderColor: "var(--mf-muted)" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Sage…"
              className="flex-1 px-4 py-2.5 rounded-full text-sm outline-none"
              style={{ background: "var(--mf-muted)", color: "var(--mf-text)" }}
              data-testid="sage-chat-input"
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50" style={{ background: "var(--mf-primary)" }} data-testid="sage-chat-send">
              <Send className="w-4 h-4 text-white" strokeWidth={1.75} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
